import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate, Outlet, Navigate } from 'react-router-dom';
import { HttpService } from 'core/services/http';
import { Utils } from 'core/utils';
import useProfile from 'features/feature_user/hooks/UseProfile';
import EmployerHeader from './EmployerHeader';
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
  Avatar
} from '@mui/material';
import { 
  LayoutDashboard, 
  Building2, 
  Briefcase, 
  ClipboardList, 
  UserSearch, 
  Users, 
  Landmark, 
  BarChart3, 
  FileText, 
  Receipt,
  ChevronRight
} from 'lucide-react';

const drawerWidth = 260;

const navItems = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/employer/dashboard' },
  { label: 'My Company', icon: Building2, path: '/employer/my-company' },
  { label: 'Jobs', icon: Briefcase, path: '/employer/jobs' },
  { label: 'Applications', icon: ClipboardList, path: '/employer/applications' },
  { label: 'Candidates', icon: UserSearch, path: '/employer/candidates' },
  { label: 'Employees', icon: Users, path: '/employer/employees' },
  { label: 'Salary Specification', icon: Landmark, path: '/employer/salary-specification' },
  { label: 'Reports', icon: BarChart3, path: '/employer/reports' },
  { label: 'Contracts', icon: FileText, path: '/employer/contracts' },
  { label: 'Invoices', icon: Receipt, path: '/employer/invoices' },
];

const EmployerDashboardLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const user = useMemo(() => HttpService.getUser?.() ?? null, []);
  const token = HttpService.getToken();
  
  // Load profile to get role
  const { data: profile, isLoading: profileLoading } = useProfile({ enabled: !!token });
  
  // Check role from localStorage (updated by UseProfile hook) or profile
  const role = useMemo(() => {
    const userRole = user?.role;
    return userRole || profile?.role;
  }, [user, profile]);
  
  const isEmployer = !!token && role === 'employer';
  
  // Show loading state while profile is being fetched
  if (profileLoading && token) {
    return null; // or a loading spinner
  }
  
  // Redirect if not an employer
  if (!isEmployer) {
    return <Navigate to="/" replace />;
  }

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2, py: 2.5 }}>
        <Typography variant="caption" fontWeight={600} color="primary">
          JOB FLOW CONNECT
        </Typography>
        {/* <Typography variant="body2" color="text.secondary">
          {user?.email ?? 'Signed in'}
        </Typography> */}
      </Box>
      <Divider />
      <List sx={{ flexGrow: 1, py: 2 }}>
        {navItems.map((item) => {
          const isActive = location.pathname === item.path || 
            (item.path !== '/employer/dashboard' && location.pathname.startsWith(item.path));
          const IconComponent = item.icon;
          return (
            <ListItemButton
              key={item.path}
              selected={isActive}
              onClick={() => handleNavigate(item.path)}
              sx={{
                borderRadius: 2,
                mx: 2,
                mb: 1,
                position: 'relative',
                transition: 'all 0.2s',
                '&.Mui-selected': {
                  bgcolor: 'primary.light',
                  color: 'primary.contrastText',
                  '& .icon-box': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText'
                  },
                  '& .chevron-icon': {
                    opacity: 1
                  },
                  '&:hover': {
                    bgcolor: 'primary.light',
                  }
                },
                '&:hover': {
                  bgcolor: 'action.hover',
                  '& .icon-box': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText'
                  },
                  '& .chevron-icon': {
                    opacity: 1
                  }
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, mr: 2 }}>
                <Box
                  className="icon-box"
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2, // rounded-lg
                    bgcolor: isActive ? 'primary.main' : 'action.selected', // bg-secondary equivalent
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isActive ? 'primary.contrastText' : 'text.secondary',
                    transition: 'all 0.2s'
                  }}
                >
                  <IconComponent size={18} />
                </Box>
              </ListItemIcon>
              <ListItemText 
                primary={item.label}
                primaryTypographyProps={{
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? 'primary.contrastText' : 'text.primary',
                  fontSize: item.label=='Salary Specification' ? '0.80rem' : '0.875rem'
                }}
              />
              <Box
                className="chevron-icon"
                sx={{
                  opacity: isActive ? 1 : 0,
                  transition: 'opacity 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  ml: 'auto'
                }}
              >
                <ChevronRight size={20} />
              </Box>
            </ListItemButton>
          );
        })}
      </List>
      <Box sx={{ px: 3, py: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Need help? Contact support@jobflowconnect.com
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header - shows on both mobile and desktop */}
      <EmployerHeader
        onMenuClick={handleDrawerToggle}
        drawerWidth={drawerWidth}
      />

      {/* Sidebar Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="employer navigation"
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth
            }
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth
            }
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Toolbar sx={{ minHeight: '72px !important' }} />
        <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default EmployerDashboardLayout;
